import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { Plus, FlowArrow, Trash, Power } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Pipelines() {
  const { data, loading, reload } = useFetch(() => api.get("/pipelines").then((r) => r.data), []);
  const [showCreate, setShowCreate] = useState(false);

  const toggleActive = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.patch(`/pipelines/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Pipeline desactivado" : "Pipeline activado");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const remove = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`¿Borrar el pipeline "${p.name}"?\n\nEsto elimina también todas las memberships (${p.members_count || 0} usuarios saldrán del pipeline). No borra los usuarios.`)) return;
    try {
      await api.delete(`/pipelines/${p.id}`);
      toast.success("Pipeline eliminado");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <>
      <PageHeader
        label="Multi-pipeline engine"
        title="Pipelines"
        description="Cada pipeline coexiste con otros. Un mismo usuario puede pertenecer a múltiples pipelines simultáneamente."
      >
        <button onClick={() => setShowCreate(true)} className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover" data-testid="create-pipeline-btn">
          <Plus size={16} /> Nuevo pipeline
        </button>
      </PageHeader>
      <PageBody>
        {loading && <div className="text-carbon-500">Cargando...</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data || []).map((p) => (
            <div key={p.id} className="relative">
              <Link to={`/pipelines/${p.id}`} className="block" data-testid={`pipeline-card-${p.slug}`}>
                <Card className="p-5 hover:border-terracotta transition-colors h-full">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-9 h-9 bg-surface-alt rounded flex items-center justify-center">
                      <FlowArrow size={18} className="text-terracotta" />
                    </div>
                    <Badge tone={p.active ? "forest" : "neutral"}>{p.active ? "activo" : "inactivo"}</Badge>
                  </div>
                  <h3 className="font-heading text-lg mb-1">{p.name}</h3>
                  <div className="text-xs text-carbon-500 mb-3 min-h-[2em]">{p.description}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span><b className="font-mono">{p.stages?.length || 0}</b> stages</span>
                    <span><b className="font-mono">{p.members_count || 0}</b> miembros</span>
                    <span><b className="font-mono">{p.ai_rules?.length || 0}</b> AI rules</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.target_role && <Badge tone="terracotta">{p.target_role}</Badge>}
                    {p.target_user_side && (
                      <Badge tone={p.target_user_side === "origin_side" ? "forest" : "terracotta"}>
                        {p.target_user_side === "origin_side" ? "origin-side" : p.target_user_side === "both" ? "both" : "migrant-side"}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-carbon-100 flex items-center gap-2">
                    <button
                      onClick={(e) => toggleActive(e, p)}
                      data-testid={`pipeline-toggle-${p.slug}`}
                      title={p.active ? "Desactivar pipeline" : "Activar pipeline"}
                      className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-carbon-200 hover:border-terracotta hover:text-terracotta transition-colors"
                    >
                      <Power size={12} /> {p.active ? "Desactivar" : "Activar"}
                    </button>
                    <button
                      onClick={(e) => remove(e, p)}
                      data-testid={`pipeline-delete-${p.slug}`}
                      title="Borrar pipeline"
                      className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-carbon-200 hover:border-signal hover:text-signal transition-colors"
                    >
                      <Trash size={12} /> Borrar
                    </button>
                  </div>
                </Card>
              </Link>
            </div>
          ))}
          {data && data.length === 0 && (
            <div className="text-sm text-carbon-500 col-span-full">Sin pipelines. Crea uno o carga el seed.</div>
          )}
        </div>
      </PageBody>

      {showCreate && <CreatePipelineModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); toast.success("Pipeline creado"); }} />}
    </>
  );
}

function CreatePipelineModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", slug: "", description: "", target_role: "", target_user_side: "migrant_side", active: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pipelines", { ...form, stages: [] });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-md p-6">
        <div className="section-label mb-1">Nuevo</div>
        <h2 className="font-heading text-2xl mb-4">Crear pipeline</h2>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="section-label">Nombre</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="section-label">Slug</span>
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })} required className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
          </label>
          <label className="block">
            <span className="section-label">Target role</span>
            <input value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })} placeholder="founder, ambassador, etc." className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="section-label">Lado del usuario</span>
            <select value={form.target_user_side} onChange={(e) => setForm({ ...form, target_user_side: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
              <option value="migrant_side">Migrant-side (USA)</option>
              <option value="origin_side">Origin-side (país origen)</option>
              <option value="both">Ambos</option>
            </select>
          </label>
          <label className="block">
            <span className="section-label">Descripción</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" rows={3} />
          </label>
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover">{busy ? "Creando..." : "Crear"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
